// src/client/components/shared/CrudDialog.tsx
import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@client/components/ui/dialog';
import { Button } from '@client/components/ui/button';
import { Save, X } from 'lucide-react';

interface CrudDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  title: string;
  description?: string;
  children: React.ReactNode;
  onSave: () => void;
  onCancel: () => void;
  isSaving?: boolean;
  saveButtonText?: string;
  cancelButtonText?: string;
  maxHeight?: string; // Optional: Custom max-height (default: 70vh)
}

export function CrudDialog({
  open,
  onOpenChange,
  mode,
  title,
  description,
  children,
  onSave,
  onCancel,
  isSaving = false,
  saveButtonText,
  cancelButtonText,
  maxHeight = '70vh',
}: CrudDialogProps) {
  const defaultSaveText = mode === 'create' ? 'Erstellen' : 'Aktualisieren';
  const defaultCancelText = 'Abbrechen';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-w-[95vw] flex flex-col gap-0 p-0 max-h-[90vh]">
        {/* Fixed Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        
        {/* Scrollable Content Area */}
        <div 
          className="flex-1 overflow-y-auto px-6 py-4 space-y-4 scroll-smooth"
          style={{ maxHeight }}
        >
          {children}
        </div>
        
        {/* Sticky Footer */}
        <DialogFooter className="px-6 py-4 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
          >
            <X className="h-4 w-4 mr-2" />
            {cancelButtonText || defaultCancelText}
          </Button>
          <Button 
            onClick={onSave}
            disabled={isSaving}
          >
            <Save className="h-4 w-4 mr-2" />
            {saveButtonText || defaultSaveText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}